import type { AgencyGraphJson } from '@saaga/shared/interfaces/agency.interface'
import { logger } from '@saaga/shared/utils/logger.util'
import type { MutableRefObject } from 'react'
import { useCallback, useState } from 'react'
import type { UseFormReturn } from 'react-hook-form'
import { toast } from 'sonner'
import type { AgencyFormSchemaType } from '@/components/agencies/agency-form.schema'

import {
  buildAgencyExportJson,
  downloadAgencyAsJson,
  readAgencyFromFile,
} from './agency-export-import.util'

interface UseAgencyExportImportParams {
  form: UseFormReturn<AgencyFormSchemaType>
  flowDataRef: MutableRefObject<AgencyGraphJson>
}

interface UseAgencyExportImportReturn {
  canvasKey: number
  handleExport: () => void
  handleImport: (file: File) => void
}

export const useAgencyExportImport = ({
  form,
  flowDataRef,
}: UseAgencyExportImportParams): UseAgencyExportImportReturn => {
  const [canvasKey, setCanvasKey] = useState(0)

  const handleExport = useCallback((): void => {
    const formValues = form.getValues()
    const exportData = buildAgencyExportJson(formValues, flowDataRef.current)
    downloadAgencyAsJson(exportData, formValues.slug)
    toast.success('Agency exported successfully')
  }, [form, flowDataRef])

  const handleImport = useCallback(
    (file: File): void => {
      readAgencyFromFile(file)
        .then(({ formFields, canvas }) => {
          const currentValues = form.getValues()
          form.reset({ ...currentValues, ...formFields }, { keepDefaultValues: true })

          if (canvas) {
            flowDataRef.current = canvas
            setCanvasKey((prev) => prev + 1)
          }

          toast.success('Agency imported successfully')
        })
        .catch((error: Error) => {
          logger.error('Failed to import agency:', error)
          toast.error(error.message)
        })
    },
    [form, flowDataRef]
  )

  return { canvasKey, handleExport, handleImport }
}
